<template>
    <LoadingCard v-if="loading" />
    <div class="card" v-else>
        <div class="card-body">
            <form @submit.prevent="attemptSave" ref="updateForm" novalidate>
                <div class="form-group row">
                    <label for="nameInput" class="col-sm-2 col-form-label">Block Name</label>
                    <div class="col-sm-10">
                        <div class="position-relative">
                            <input type="text" id="nameInput" name="name" v-validate="validation.name" v-model="name"
                                   placeholder="Block Name" class="form-control" required>
                            <div class="invalid-feedback" v-if="errors.has('name')">{{ errors.first('name') }}</div>
                            <i class="form-group__bar"></i>
                        </div>
                    </div>
                </div>

                <div class="form-group row">
                    <label class="col-sm-2 col-form-label">Block District</label>

                    <div class="col-sm-10">
                        <div class="position-relative">
                            <VueSelect
                                    v-validate="{required: true}"
                                    data-vv-name="district"
                                    v-model="district"
                                    :options="districts"
                                    :allow-empty="false"
                                    deselect-label=""
                                    placeholder="District"
                                    label="name"
                                    track-by="id"
                            >
                            </VueSelect>
                            <div class="invalid-feedback" v-if="errors.has('district')">{{ errors.first('district') }}</div>
                            <i class="form-group__bar"></i>
                        </div>
                    </div>
                </div>

                <div class="form-group row">
                    <label class="col-sm-2 col-form-label">Block Crop Type</label>
                    <div class="col-sm-10">
                        <input type="text" :value="cropType.name" class="form-control form-control-static" readonly>
                        <i class="form-group__bar"></i>
                    </div>
                </div>

                <div class="form-group row">
                    <label class="col-sm-2 col-form-label">Block Variety</label>
                    <div class="col-sm-10">
                        <div class="position-relative">
                            <VueSelect
                                    v-validate="validation.variety"
                                    data-vv-name="variety"
                                    v-model="variety"
                                    :options="cropType.varietyGroups"
                                    :allow-empty="false"
                                    deselect-label=""
                                    placeholder="Block Variety"
                                    group-values="varieties"
                                    group-label="name"
                                    label="name"
                                    track-by="id"
                            >
                            </VueSelect>
                            <div class="invalid-feedback" v-if="errors.has('variety')">{{ errors.first('variety') }}</div>
                            <i class="form-group__bar"></i>
                        </div>
                    </div>
                </div>

                <div class="form-group row">
                    <label class="col-sm-2 col-form-label">Year Planted</label>
                    <div class="col-sm-10">
                        <div class="position-relative">
                            <VueMask v-model.number="year_planted" class="form-control"
                                     :class="{'is-invalid': errors.has('year_planted')}"
                                     placeholder="0..."
                                     v-validate="validation.year_planted"
                                     data-vv-name="year_planted"
                                     data-vv-as="year planted"
                                     :options="{date: true, datePattern: ['Y'], stripLeadingZeroes: true}" />
                            <div class="invalid-feedback" v-if="errors.has('year_planted')">{{ errors.first('year_planted') }}</div>
                            <i class="form-group__bar"></i>
                        </div>
                    </div>
                </div>

                <div class="form-group row">
                    <label class="col-sm-2 col-form-label">Total Area</label>
                    <div class="col-sm-10">
                        <div class="position-relative">
                            <VueMask v-model.number="area" class="form-control"
                                     :class="{'is-invalid': errors.has('area')}"
                                     placeholder="0 hectares"
                                     v-validate="validation.area"
                                     data-vv-name="area"
                                     data-vv-as="area"
                                     :options="{numeral: true, numeralPositiveOnly: true}" />
                            <div class="invalid-feedback" v-if="errors.has('area')">{{ errors.first('area') }}</div>
                            <i class="form-group__bar"></i>
                        </div>
                    </div>
                </div>

                <div class="form-group row">
                    <label class="col-sm-2 col-form-label">Tree Number</label>
                    <div class="col-sm-10">
                        <div class="position-relative">
                            <VueMask v-model.number="num_trees" class="form-control"
                                     :class="{'is-invalid': errors.has('num_trees')}"
                                     placeholder="0 trees"
                                     :options="{numeral: true, numeralPositiveOnly: true, numeralDecimalScale: 0}" />
                            <i class="form-group__bar"></i>
                        </div>
                    </div>
                </div>

                <div class="form-group row">
                    <label class="col-sm-2 col-form-label">Tree Spacing</label>
                    <div class="col-sm-10">
                        <div class="position-relative">
                            <VueMask v-model.number="tree_spacing" class="form-control"
                                     :class="{'is-invalid': errors.has('tree_spacing')}"
                                     placeholder="0 meters"
                                     :options="{numeral: true, numeralPositiveOnly: true}" />
                            <i class="form-group__bar"></i>
                        </div>
                    </div>
                </div>

                <div class="form-group row">
                    <label class="col-sm-2 col-form-label">Row Spacing</label>
                    <div class="col-sm-10">
                        <div class="position-relative input-group">
                            <VueMask v-model.number="row_spacing" class="form-control"
                                     :class="{'is-invalid': errors.has('row_spacing')}"
                                     placeholder="0 meters"
                                     :options="{numeral: true, numeralPositiveOnly: true}" />
                            <i class="form-group__bar"></i>
                        </div>
                    </div>
                </div>

                <div class="form-group row">
                    <label class="col-sm-2 col-form-label">Canopy Area</label>
                    <div class="col-sm-10">
                        <div class="position-relative">
                            <VueMask :value="canopyArea" class="form-control" readonly placeholder="0 hectares"
                                     :options="{numeral: true, numeralPositiveOnly: true, numeralDecimalScale: 3}" />
                            <i class="form-group__bar"></i>
                        </div>
                    </div>
                </div>

                <div class="text-right">
                    <button type="button" class="btn btn-link text-danger" @click="handleDelete">Delete</button>
                    <button type="submit" class="btn btn-success">Update</button>
                </div>
            </form>
        </div>
    </div>
</template>
<script>
import deleteBlockMutation from '@views/Blocks/DetailsManager/mutationDeleteBlock.graphql';
import updateBlockMutation from '@views/Blocks/DetailsManager/mutationUpdateBlock.graphql';
import currentUserQuery from '@views/Blocks/DetailsManager/queryCurrentUser.graphql';
import findCropTypeQuery from '@views/Blocks/DetailsManager/queryFindCropType.graphql';

export default {
  name: 'DetailsManager',
  props: ['block'],
  apollo: {
    currentUser: {
      query: currentUserQuery,
      update: data => data.currentUser,
    },
    cropType: {
      query: findCropTypeQuery,
      update: data => data.findCropType,
      variables() {
        return {
          input: {
            id: this.block.cropType.id,
          },
        };
      },
    },
  },
  data() {
    return {
      loading: 0,
      name: this.block.name,
      variety: this.block.variety,
      year_planted: this.block.year_planted,
      area: this.block.area,
      num_trees: this.block.num_trees,
      row_spacing: this.block.row_spacing,
      tree_spacing: this.block.tree_spacing,
      district: this.block.district,
      cropType: null,
      validation: {
        year_planted: { between: [1970, (new Date()).getFullYear()] },
        name: { required: true },
        variety: { required: true },
      },
    };
  },
  computed: {
    districts() {
      return this.currentUser.region.districts || [];
    },
    canopyArea() {
      return this.row_spacing * this.tree_spacing * this.num_trees / 10000;
    },
  },
  methods: {
    attemptSave() {
      this.errors.remove('global');

      this.$validator.validateAll().then(validationWasSuccessful => {
        // this.$refs.accountForm.classList.add('was-validated');
        if (!validationWasSuccessful) {
          return;
        }

        this.$apollo.mutate({
            mutation: updateBlockMutation,
            variables: {
              input: {
                id: this.block.id,
                name: this.name,
                variety_id: this.variety.id,
                year_planted: this.year_planted,
                area: this.area,
                num_trees: this.num_trees,
                row_spacing: this.row_spacing,
                tree_spacing: this.tree_spacing,
                district_id: this.district.id,
              },
            },
            optimisticResponse: {
              __typename: 'Mutation',
              updateBlock: {
                __typename: 'Block',
                id: this.block.id,
                name: this.name,
                variety: this.variety,
                variety_id: this.variety.id,
                cropType: this.cropType,
                year_planted: this.year_planted,
                area: this.area,
                num_trees: this.num_trees,
                row_spacing: this.row_spacing,
                tree_spacing: this.tree_spacing,
                district_id: this.district.id,
              },
            },
          })
          .then(() => {
            this.$notifySuccess('Changes saved!');
          })
          .catch(err => {
            this.$mapGraphQLError(err);
          })
          .catch(err => {
            console.error(err);
            this.$notifyFailure(
              'Unfortunately, we were unable to do that!',
            );
          });
      });
    },
    handleDelete() {
      this.$apollo
        .mutate({
          mutation: deleteBlockMutation,
          variables: {
            input: {
              id: this.block.id,
            },
          },
          optimisticResponse: {
            __typename: 'Mutation',
            deleteBlock: {
              __typename: 'Block',
              id: this.block.id,
              deleted_at: '1970-01-01 00:00:00',
            },
          },
        })
        .then(() => {
          this.$notifySuccess('Block Deleted!');
          this.$router.back();
        })
        .catch(err => {
          this.$mapGraphQLError(err);
        })
        .catch(err => {
          console.error(err);
          this.$notifyFailure(
            'Unfortunately, we were unable to do that!',
          );
        });
    },
  },
};
</script>
